import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Alert,
  Table,
  Badge,
} from "reactstrap";

// Import shared BankFormModal
import { BankFormModal } from './shared';

// Import NotificationBanner
import NotificationBanner from '../../../components/NotificationBanner';

// Import API services
import apiService from '../../../service/apiService';
import apiConfig, { buildEndpointUrl } from '../../../config/apiConfig';

const BankInformationModalTab = ({ merchantId, isReadOnly = false, onRefresh }) => {
  // State for bank information
  const [banks, setBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

  // Load bank information when component mounts
  useEffect(() => {
    if (merchantId) {
      loadBankInformation();
    }
  }, [merchantId]);

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ show: false, type: 'success', message: '' });
  };

  // Function to load bank information
  const loadBankInformation = async () => {
    setIsLoading(true);
    setLoadError(null);
    
    try {
      // ✅ Usar apiService con buildEndpointUrl para la URL correcta
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.banks(merchantId));
      const data = await apiService.get(endpoint);
      
      console.log('🔍 DEBUG - Banks API Response:', data);
      console.log('🔍 DEBUG - Response type:', typeof data);
      console.log('🔍 DEBUG - Is array:', Array.isArray(data));
      
      // Handle different response structures
      let banksData = data;
      if (data && data.content && Array.isArray(data.content)) {
        // If response is paginated
        banksData = data.content;
      } else if (data && Array.isArray(data)) {
        // If response is direct array
        banksData = data;
      } else if (data && data.banks && Array.isArray(data.banks)) {
        // If response has banks property
        banksData = data.banks;
      } else {
        // Fallback
        banksData = [];
      }
      
      console.log('🔍 DEBUG - Processed banks data:', banksData);
      setBanks(banksData);
    } catch (error) {
      console.error('Error loading bank information:', error);
      setLoadError('Failed to load bank information');
      showNotification('error', 'Failed to load bank information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bank added callback
  const handleBankAdded = async () => {
    console.log('🔄 handleBankAdded called - refreshing bank information');
    try {
      // Refresh bank information after adding new bank
      await loadBankInformation();
      showNotification('success', 'Bank information added successfully!');
    } catch (error) {
      console.error('Error refreshing bank information:', error);
      showNotification('error', 'Bank added but failed to refresh the list. Please reload the page.');
    }
  };

  // Handle add bank button click
  const handleAddBankClick = () => {
    console.log('🔘 Add Bank button clicked, opening modal');
    setShowAddModal(true);
  };

  // Render bank information display
  const renderBankInformation = () => {
    if (isLoading) {
      return (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading bank information...</p>
        </div>
      );
    }

    if (loadError) {
      return (
        <Alert color="danger">
          <i className="ni ni-alert-circle mr-2" />
          {loadError}
          <Button color="link" className="ml-2" onClick={loadBankInformation}>
            Try Again
          </Button>
        </Alert>
      );
    }

    if (banks.length === 0) {
      return (
        <div className="text-center py-4">
          <i className="fas fa-university text-muted" style={{ fontSize: '2rem' }}></i>
          <p className="mt-2 text-muted">No bank information available</p>
        </div>
      );
    }

    return (
      <Row>
        {banks.map((bank, index) => (
          <Col md="12" key={index} className="mb-4">
            <Card className="border shadow-sm">
              <CardHeader style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb' }}>
                  <div className="d-flex align-items-center">
                    <i className="ni ni-credit-card text-muted mr-3" style={{ fontSize: '1.5rem' }} />
                    <h5 className="mb-0 text-dark truncate-text" style={{ fontSize: '1rem' }}>{bank.bankName || 'Bank Name'}</h5>
                  </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col lg="6" md="12" className="mb-3 mb-lg-0">
                    <Table borderless size="sm">
                      <tbody>
                          <tr>
                            <td className="font-weight-bold" width="40%">Account Type:</td>
                            <td>
                              <Badge color="secondary" className="badge-pill">
                                {bank.type || 'N/A'}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td className="font-weight-bold" width="40%">Account Name:</td>
                            <td className="text-break">{bank.accountName || 'N/A'}</td>
                          </tr>
                          
                        <tr>
                          <td className="font-weight-bold">Account Number:</td>
                          <td className="text-break">{bank.number ? `****${bank.number.slice(-4)}` : 'N/A'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col lg="6" md="12">
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="font-weight-bold" width="40%">Bank City:</td>
                          <td className="text-break">{bank.bankCity || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="font-weight-bold">Routing Number:</td>
                          <td className="text-break">{bank.routingNumber ? `****${bank.routingNumber.slice(-4)}` : 'N/A'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container fluid>
      <style>
        {`
          @media (max-width: 767.98px) {
            .card-body {
              padding: 0.75rem !important;
            }
            .btn {
              min-height: 36px !important;
              font-size: 0.8rem !important;
              padding: 0.4rem 0.6rem !important;
            }
            .table {
              font-size: 0.875rem !important;
            }
            
            /* Header responsivo */
            .card-header .d-flex {
              flex-direction: column !important;
              align-items: flex-start !important;
            }
            .card-header .d-flex > div:last-child {
              margin-top: 0.5rem !important;
              width: 100% !important;
            }
            
            /* Botón Add Bank responsivo */
            .btn-primary {
              width: auto !important;
              min-width: 120px !important;
            }
            
            /* Cards de información bancaria responsivas */
            .card.border {
              margin-bottom: 1rem !important;
            }
            
            .card.border .card-header {
              padding: 0.75rem !important;
            }
            
            .card.border .card-body {
              padding: 0.75rem !important;
            }
            
            /* Reparar corte en móviles */
            .container-fluid {
              padding-left: 0.5rem !important;
              padding-right: 0.5rem !important;
            }
            
            .card {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            
            /* Row responsivo para información bancaria */
            .card.border .row {
              margin: 0 !important;
            }
            
            .card.border .row .col-md-6 {
              width: 100% !important;
              margin-bottom: 1rem !important;
              padding: 0 !important;
            }
            
            .card.border .row .col-md-6:last-child {
              margin-bottom: 0 !important;
            }
          }
          
          /* Mejoras para rango 768px-991px */
          @media (max-width: 991.98px) and (min-width: 768px) {
            .card.border .row .col-md-6 {
              width: 100% !important;
              margin-bottom: 1rem !important;
              padding: 0 !important;
            }
            
            .card.border .row .col-md-6:last-child {
              margin-bottom: 0 !important;
            }
            
            /* Tablas responsivas para tablets */
            .table {
              font-size: 0.85rem !important;
            }
            
            .table td, .table th {
              padding: 0.5rem 0.3rem !important;
            }
            
            /* Badges responsivos para tablets */
            .badge {
              font-size: 0.75rem !important;
            }
            
            /* Tablas responsivas */
            .table {
              font-size: 0.8rem !important;
            }
            
            /* Bank Information Text Overflow Fix */
            .bank-info-card {
              overflow: hidden !important;
            }
            
            .bank-info-card .card-body {
              overflow-wrap: break-word !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              overflow: hidden !important;
            }
            
            .bank-info-card .table {
              table-layout: fixed !important;
              width: 100% !important;
            }
            
            .bank-info-card .table td {
              max-width: 0 !important;
              overflow-wrap: break-word !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              overflow: hidden !important;
              white-space: normal !important;
            }
            
            .bank-info-card .text-break {
              overflow-wrap: break-word !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              max-width: 100% !important;
              overflow: hidden !important;
              white-space: normal !important;
            }
            
            /* Asegurar que las celdas de la tabla no se salgan */
            .bank-info-card .table td:first-child {
              width: 40% !important;
              min-width: 40% !important;
            }
            
            .bank-info-card .table td:last-child {
              width: 60% !important;
              min-width: 60% !important;
            }
            
            .table td, .table th {
              padding: 0.4rem 0.25rem !important;
            }
            
            /* Badges responsivos */
            .badge {
              font-size: 0.7rem !important;
            }
          }
          
          @media (max-width: 575.98px) {
            .card-body {
              padding: 0.5rem !important;
            }
            .btn {
              font-size: 0.75rem !important;
              padding: 0.3rem 0.5rem !important;
              min-height: 32px !important;
            }
            
            /* Cards de información bancaria en móviles */
            .card.border .card-header {
              padding: 0.5rem !important;
            }
            
            .card.border .card-body {
              padding: 0.5rem !important;
            }
            
            .card.border .card-header h5 {
              font-size: 0.9rem !important;
            }
            
            .card.border .card-header small {
              font-size: 0.75rem !important;
            }
            
            /* Botón Add Bank en móviles */
            .btn-primary {
              min-width: 100px !important;
              font-size: 0.7rem !important;
            }
            
            /* Tablas en móviles */
            .table {
              font-size: 0.7rem !important;
            }
            
            .table td, .table th {
              padding: 0.3rem 0.2rem !important;
            }
            
            /* Badges en móviles */
            .badge {
              font-size: 0.65rem !important;
            }
          }
          
          /* Estilos globales para prevenir overflow en todos los tamaños */
          .bank-info-card {
            max-width: 100% !important;
            overflow: hidden !important;
          }
          
          .bank-info-card * {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          .bank-info-card .table {
            width: 100% !important;
            table-layout: fixed !important;
            overflow: hidden !important;
          }
          
          .bank-info-card .table td {
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            hyphens: auto !important;
            overflow: hidden !important;
            white-space: normal !important;
          }
        `}
      </style>
      
      {/* Notification Banner */}
      <NotificationBanner
        type={notification.type}
        message={notification.message}
        show={notification.show}
        onClose={closeNotification}
      />

      {/* Bank Information Display */}
      <Row>
        <Col md="12">
          <Card className="shadow-sm">
            <CardHeader style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb' }} className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <i className="ni ni-credit-card text-muted mr-3" style={{ fontSize: '1.5rem' }} />
                <h5 className="mb-0 text-dark" style={{ fontSize: '1rem' }}>Bank Information</h5>
              </div>
              {!isReadOnly && (
                <Button color="primary" size="sm" onClick={handleAddBankClick}>
                  <i className="ni ni-plus mr-1"></i>
                  Add Bank
                </Button>
              )}
            </CardHeader>
            <CardBody>

              {renderBankInformation()}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Use shared BankFormModal */}
      <BankFormModal
        isOpen={showAddModal}
        toggle={() => setShowAddModal(false)}
        onSave={handleBankAdded}
        title="Add Bank Information"
        apiEndpoint={buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.createBank(merchantId))}
        apiService={apiService}
      />
    </Container>
  );
};

export default BankInformationModalTab;
